import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, CssBaseline, Container } from '@mui/material';
import ThemeProvider from './ThemeProvider';
import NewStoreHeader from './NewStoreHeader';
import NewStoreFooter from './NewStoreFooter';
import HomeSlider from './HomeSlider';
import FeaturedProducts from './FeaturedProducts';
import CategoryShowcase from './CategoryShowcase';
import BestSellers from './BestSellers';
import InfoBar from './InfoBar';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import { updateFavicon } from '../../utils/faviconUtils';

const NewStoreLayout: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  // استخراج إعدادات المتجر
  const { settings } = useStoreSettings();
  const location = useLocation();

  // تعيين عنوان الصفحة والأيقونة بناءً على إعدادات المتجر
  useEffect(() => {
    if (settings?.general?.storeName) {
      // استخدام اسم الشركة في عنوان المتصفح
      document.title = settings.general.storeName;

      // استخدام أيقونة افتراضية
      const faviconUrl = '/favicon.ico';

      // إضافة أيقونة المتجر
      const existingFavicon = document.querySelector('link[rel="icon"]');
      if (existingFavicon) {
        // إذا كان هناك أيقونة موجودة، قم بتحديثها
        // نحاول استخدام ملف الأيقونة أولاً، وإذا فشل نستخدم الأيقونة الديناميكية
        try {
          const faviconImg = new Image();
          faviconImg.src = `${process.env.PUBLIC_URL}/favicon.ico?v=${Date.now()}`;
          faviconImg.onerror = () => {
            existingFavicon.setAttribute('href', faviconUrl);
          };
          faviconImg.onload = () => {
            existingFavicon.setAttribute('href', `${process.env.PUBLIC_URL}/favicon.ico?v=${Date.now()}`);
          };
        } catch (error) {
          existingFavicon.setAttribute('href', faviconUrl);
        }
      } else {
        // إذا لم تكن هناك أيقونة، قم بإنشاء واحدة جديدة
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.href = faviconUrl;
        document.head.appendChild(favicon);
      }

      // إضافة أيقونة shortcut icon إذا لم تكن موجودة
      if (!document.querySelector('link[rel="shortcut icon"]')) {
        const shortcutIcon = document.createElement('link');
        shortcutIcon.rel = 'shortcut icon';
        shortcutIcon.href = faviconUrl;
        document.head.appendChild(shortcutIcon);
      }
    }

    // إعادة تحميل الأيقونة كل 5 ثوانٍ
    const intervalId = setInterval(() => {
      const icons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
      icons.forEach(icon => {
        const href = icon.getAttribute('href');
        if (href) {
          const newHref = href.split('?')[0] + `?v=${Date.now()}`;
          icon.setAttribute('href', newHref);
        }
      });
    }, 5000);

    // تنظيف الفاصل الزمني عند إلغاء تحميل المكون
    return () => clearInterval(intervalId);
  }, [settings]);

  // تحديث الأيقونة عند تغيير الصفحة
  useEffect(() => {
    // تحديث جميع الأيقونات في كل مرة يتغير فيها المسار
    const icons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
    icons.forEach(icon => {
      const href = icon.getAttribute('href');
      if (href) {
        // إضافة معلمة عشوائية لمنع التخزين المؤقت
        const newHref = href.split('?')[0] + `?v=${Date.now()}`;
        icon.setAttribute('href', newHref);
      }
    });
  }, [location.pathname]);

  // معالجة البحث
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // يمكن استخدام هذه الدالة لاحقاً عند تنفيذ وظيفة تصفية المنتجات حسب الفئة
  // const handleCategorySelect = (categoryId: number) => {
  //   setSelectedCategoryId(categoryId);
  // };

  // تحديد ما إذا كنا في الصفحة الرئيسية
  const isHomePage = window.location.pathname === '/store-new' || window.location.pathname === '/store-new/';

  return (
    <ThemeProvider>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <CssBaseline />

          {/* رأس المتجر الجديد */}
          <NewStoreHeader
            onSearch={handleSearch}
          />

          {/* محتوى الصفحة */}
          <Box sx={{ flexGrow: 1 }}>
            {isHomePage ? (
              // محتوى الصفحة الرئيسية
              <>
                {/* السلايدر الرئيسي */}
                <HomeSlider />

                {/* شريط المعلومات */}
                <InfoBar />

                {/* عرض الأقسام */}
                <CategoryShowcase title="تسوق حسب الأقسام" />

                {/* المنتجات المميزة - عرض كامل */}
                <Box sx={{
                  width: '100vw',
                  marginLeft: 'calc(-50vw + 50%)',
                  marginRight: 'calc(-50vw + 50%)'
                }}>
                  <FeaturedProducts title="المنتجات المميزة" maxItems={8} />
                </Box>

                {/* المنتجات الأكثر مبيعاً - عرض كامل */}
                <Box sx={{
                  backgroundColor: '#f5f5f5',
                  py: 6,
                  width: '100vw',
                  marginLeft: 'calc(-50vw + 50%)',
                  marginRight: 'calc(-50vw + 50%)'
                }}>
                  <BestSellers title="الأكثر مبيعاً" maxItems={4} showBadge={true} />
                </Box>

                {/* وصل حديثاً - عرض كامل */}
                <Box sx={{
                  width: '100vw',
                  marginLeft: 'calc(-50vw + 50%)',
                  marginRight: 'calc(-50vw + 50%)'
                }}>
                  <FeaturedProducts title="وصل حديثاً" maxItems={8} />
                </Box>

                {/* شريط معلومات سفلي */}
                <Box sx={{ mt: 4 }}>
                  <InfoBar />
                </Box>
              </>
            ) : (
              // محتوى الصفحات الأخرى بعرض كامل
              <Container maxWidth={false} sx={{ py: 3, px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
                <Outlet context={{ searchQuery }} />
              </Container>
            )}
          </Box>

          {/* تذييل المتجر */}
          <NewStoreFooter />
        </Box>
    </ThemeProvider>
  );
};

export default NewStoreLayout;
